import { GeoPoint, Property } from '../interfaces/interfaces';

export function toTitleCase(str: string | undefined) {
	if (!str) {
		return '';
	}

	return str.replace(/\w\S*/g, function (txt) {
		return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
	});
}

export function buildPropertyDetailsUrl(property: Property) {
	if (property) {
		if (property.AddressUrlString) {
			return `/real-estate/${property.AddressUrlString}`;
		}
		let addressLineOne = '';
		const addressParts: any[] = [];

		addressParts.push(property.StreetNumber);

		if (property.StreetDirPrefix) {
			addressParts.push(toTitleCase(property.StreetDirPrefix));
		}

		addressParts.push(toTitleCase(property.StreetName));
		addressParts.push(property.StreetSuffix ? toTitleCase(property.StreetSuffix) : '');

		if (property.StreetDirSuffix) {
			addressParts.push(toTitleCase(property.StreetDirSuffix));
		}

		if (property.UnitNumber) {
			addressParts.push('Unit');
			addressParts.push(property.UnitNumber);
		}

		const addressLineOneWithSpaces = addressParts.join(' ');
		addressLineOne = addressLineOneWithSpaces.replace(/\s/g, '-').replace(/\//g, '-');

		const city = toTitleCase(property?.City?.replace(/\s/g, '-'));
		const propertyAddress = `${addressLineOne}-${city}-${property?.StateOrProvince}-${property?.PostalCode}`;

		return `/property-details/${encodeURIComponent(propertyAddress)}/${property?.id}`;
	} else {
		return '';
	}
}

export function buildPropertyDetailsUrlWithPrevUrl(property: Property) {
	const currentUrl = window.location.pathname.replace('/homes', '');

	return buildPropertyDetailsUrl(property) + `?prevUrl=${encodeURIComponent(currentUrl)}`;
}

export function normalizeGeoPoint(geopoint: any): GeoPoint {
	if (!geopoint.lat || (!geopoint.lng && !geopoint.lon)) throw new Error('Geopoint invalid');
	return { lat: geopoint.lat, lng: geopoint.lng ?? geopoint.lon };
}

export const isCapCenterListOffice = (
	listOfficeName: string | undefined,
	listOfficeMlsId: string | undefined,
	originatingSystemName: string | undefined,
) => {
	const capcenterKeys = ['capital center', 'capcenter', 'cap center'];

	if (!listOfficeName && !listOfficeMlsId) return false;

	if (listOfficeName) {
		const listOfficeNameLowercase = listOfficeName.toLowerCase();

		for (const key of capcenterKeys) {
			if (listOfficeNameLowercase.includes(key)) return true;
		}
	}

	if (listOfficeMlsId) {
		return (
			isCapCenterOffice(listOfficeMlsId, originatingSystemName) &&
      (!listOfficeName ||
        listOfficeName?.toLowerCase().includes('cap') ||
        listOfficeName?.toLowerCase().includes('circle'))
		);
	}

	return false;
};

export const isCapCenterOffice = (officeMlsId: string | undefined, originatingSystemName: string | undefined) => {
	const capcenterMlsOffices = [
		{
			originatingSystemName: 'REIN',
			officeMlsId: '129380',
		},
		{
			originatingSystemName: 'ColumbiaSC',
			officeMlsId: '1461',
		},
		{
			originatingSystemName: 'GreenvilleSC',
			officeMlsId: '2392',
		},
		{
			originatingSystemName: 'TriangleMLS',
			officeMlsId: '77277',
		},
		{
			originatingSystemName: 'CTAR',
			officeMlsId: '9452',
		},
		{
			originatingSystemName: 'CentralVirginia',
			officeMlsId: 'CAPC01',
		},
		{
			originatingSystemName: 'FMLS',
			officeMlsId: 'CAPC01',
		},
		{
			originatingSystemName: 'BrightMLS',
			officeMlsId: 'CIRCB1',
		},
		{
			originatingSystemName: 'GeorgiaMLS',
			officeMlsId: 'CPCN01',
		},
		{
			originatingSystemName: 'BrightMLS',
			officeMlsId: 'CPTT1',
		},
		{
			originatingSystemName: 'TriadMLS',
			officeMlsId: 'GCAPCEN01',
		},
		{
			originatingSystemName: 'CharlottesvilleVA',
			officeMlsId: 'K10215',
		},
		{
			originatingSystemName: 'CentralVirginia',
			officeMlsId: 'LOAN87',
		},
		{
			originatingSystemName: 'NorthCarolinaRegional',
			officeMlsId: 'o847502762',
		},
		{
			originatingSystemName: 'CanopyMLS',
			officeMlsId: 'R00079',
		},
		{
			originatingSystemName: 'CanopyMLS',
			officeMlsId: 'R00080',
		},
	];

	if (officeMlsId && originatingSystemName) {
		for (const capcenterMlsOffice of capcenterMlsOffices) {
			if (
				capcenterMlsOffice.officeMlsId.toLowerCase() === officeMlsId.toLowerCase() &&
        capcenterMlsOffice.originatingSystemName.toLowerCase() === originatingSystemName.toLowerCase()
			) {
				return true;
			}
		}
	}

	return false;
};

export const mapOriginatingSystemToSource: any = {
	CentralVirginia: 'CVRMLS',
	BrightMLS: 'BRIGHT',
	TriangleMLS: 'TMLS',
	REIN: 'REIN',
	CTAR: 'CTARMLS',
	GreenvilleSC: 'GGAR',
	CharlottesvilleVA: 'CAAR',
	CanopyMLS: 'CMLS',
	TriadMLS: 'TRIAD',
	ColumbiaSC: 'CML',
	FMLS: 'FMLS',
	GeorgiaMLS: 'GAMLS',
	NorthCarolinaRegional: 'NCRMLS',
};

export const stripListingIdPrefix = (listingId: string) => {
	const prefixesToStrip = [/^CAR/];

	prefixesToStrip.forEach(prefix => {
		listingId = listingId.replace(prefix, '');
	});

	return listingId;
};
